import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["CQC", "Ofsted", "Clinical Aesthetics", "GP & Hospitals", "Fostering", "Supported Living", "Mock Interview", "Start-up"];
const TYPES = [
  { id: "policy", label: "Policy pack" }, { id: "resource", label: "Resource / toolkit" }, { id: "mock_interview", label: "Mock interview" },
];
const EMPTY = { title: "", description: "", category: "CQC", type: "policy", price_gbp: "", pages: "", toc: "", active: true };

export default function AdminResourceForm({ open, resource, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(resource ? {
      title: resource.title, description: resource.description, category: resource.category, type: resource.type,
      price_gbp: String(resource.price_gbp), pages: String(resource.pages || ""), toc: (resource.toc || []).join("\n"),
      active: resource.active !== false,
    } : EMPTY);
  }, [open, resource]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    const payload = {
      title: form.title.trim(), description: form.description.trim(), category: form.category, type: form.type,
      price_gbp: Number(form.price_gbp), pages: Number(form.pages) || 0,
      toc: form.toc.split("\n").map((s) => s.trim()).filter(Boolean), active: form.active,
    };
    if (!payload.title || !payload.description || !(payload.price_gbp > 0)) { toast.error("Title, description and a price above £0 are required"); return; }
    setSaving(true);
    try {
      if (resource) await api.put(`/admin/resources/${resource.id}`, payload);
      else await api.post("/admin/resources", payload);
      toast.success(resource ? "Resource updated" : "Resource added to the store");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="resource-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{resource ? "Edit resource" : "Add a resource"}</DialogTitle>
          <DialogDescription>Prices sync to Stripe automatically. Changing a price creates a new Stripe price for future purchases.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <Label htmlFor="rf-title">Title</Label>
            <Input id="rf-title" value={form.title} onChange={set("title")} className="mt-1.5" data-testid="resource-form-title" />
          </div>
          <div>
            <Label htmlFor="rf-desc">Description</Label>
            <Textarea id="rf-desc" value={form.description} onChange={set("description")} rows={3} className="mt-1.5" data-testid="resource-form-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rf-cat">Category</Label>
              <select id="rf-cat" value={form.category} onChange={set("category")} data-testid="resource-form-category"
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="rf-type">Type</Label>
              <select id="rf-type" value={form.type} onChange={set("type")} data-testid="resource-form-type"
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="rf-price">Price (£)</Label>
              <Input id="rf-price" type="number" min="1" step="0.5" value={form.price_gbp} onChange={set("price_gbp")} className="mt-1.5" data-testid="resource-form-price" />
            </div>
            <div>
              <Label htmlFor="rf-pages">Pages (0 for none)</Label>
              <Input id="rf-pages" type="number" min="0" value={form.pages} onChange={set("pages")} className="mt-1.5" data-testid="resource-form-pages" />
            </div>
          </div>
          <div>
            <Label htmlFor="rf-toc">What's included (one per line)</Label>
            <Textarea id="rf-toc" value={form.toc} onChange={set("toc")} rows={4} className="mt-1.5 font-mono text-xs" data-testid="resource-form-toc" />
          </div>
          <label className="flex items-center justify-between rounded-lg border border-border p-3">
            <span className="text-sm">Visible in the store</span>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="resource-form-active" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="resource-form-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> {resource ? "Save changes" : "Add resource"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
